import React, { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { amazonAPI } from '../api/amazonAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { formatDateTime } from '../utils/commonUtils'
import { AmazonSettingsSectionHeader } from './AmazonScopeChip'

const BIDDING_PATH = '/amazon-ad-automation/bidding'
const HUB_PATH = '/amazon-ad-automation'

const Stat = ({ label, value }) => (
    <Col xs={6} md={3} className="mb-2">
        <div className="small text-body-secondary">{label}</div>
        <div className="fs-5 fw-semibold">{value}</div>
    </Col>
)

/**
 * Inventory-Aware Bidding tab of Ad Automation Settings: a status view for one account. Bidding is
 * configured per enrolled campaign on the bidding page, next to the evaluations it controls.
 */
function AmazonBiddingStatusPanel({ accountId, accountName, backTo }) {
    const navigate = useNavigate()
    const [summary, setSummary] = useState(null)
    const [openAlerts, setOpenAlerts] = useState(null)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!accountId) return
        setLoading(true)
        setError('')
        Promise.all([
            amazonAPI.bidding.getDashboardSummary(accountId),
            amazonAPI.alerts.getAll({ account_id: accountId, source: 'bidding', status: 'open', kind: 'all', page_size: 1 }),
        ])
            .then(([s, alerts]) => { setSummary(s); setOpenAlerts(alerts?.count ?? 0) })
            .catch(err => setError(formatErrorResponse(err)))
            .finally(() => setLoading(false))
    }, [accountId])

    if (loading) return <div className="text-center py-5"><Spinner animation="border" /></div>
    if (error) return <Alert variant="danger">{error}</Alert>
    if (!summary) return null

    const openBidding = () => navigate(`${BIDDING_PATH}?account=${accountId}`, {
        state: { accountId: Number(accountId), accountName, backTo, backLabel: 'Back to Ad Automation Settings' },
    })
    const showAlerts = () => navigate(`${HUB_PATH}?account=${accountId}&source=bidding&kind=all`)

    return (
        <div style={{ maxWidth: '730px' }}>
            <Card className="mb-4">
                <Card.Body>
                    <AmazonSettingsSectionHeader
                        title="Inventory-Aware Bidding"
                        scope="bidding"
                        scopeLabel="Inventory-Aware Bidding · per campaign"
                        sentence="Each enrolled campaign has its own lookback, stock threshold and bid decrease. They are edited on the bidding page, next to the evaluations they control. Daily list limits and product exceptions do not apply to bidding."
                    />
                    <Row>
                        <Stat label="Active campaigns" value={summary.enrolled_count} />
                        <Stat label="Paused campaigns" value={summary.inactive_count ?? 0} />
                        <Stat label="Last evaluation" value={summary.last_evaluated_at ? formatDateTime(summary.last_evaluated_at) : ''} />
                        <Stat label="Open bidding alerts" value={openAlerts ?? 0} />
                    </Row>
                    <div className="small text-body-secondary mb-3">
                        Latest results: {summary.ok_count} OK · {summary.decrease_count} bid decrease recommended · {summary.error_count} error
                        {summary.never_evaluated_count ? ` · ${summary.never_evaluated_count} not evaluated yet` : ''}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <Button variant="primary" onClick={openBidding}>
                            <i className="fas fa-pen me-2"></i>Edit bidding campaigns
                        </Button>
                        <Button variant="outline-primary" onClick={showAlerts} disabled={!openAlerts}>
                            Show open bidding alerts
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        </div>
    )
}

export default AmazonBiddingStatusPanel
